import { ReactNode } from 'react';
import Head from 'next/head';
import { Spin } from 'antd';
import { useOnboardingStateContext } from 'context/onboarding/OnboardingStateContext';
import styled from 'styled-components';

import { EverysetLogo } from '@assets/img';
import SaaSLogo from '@assets/img/SaaSLogo';
import { useClientEnvironment } from '@hooks/clientEnvironment';
import { useAuth } from '@lib/auth';
import { extractSubdomainfromUrl } from '@lib/utils/urlHelper';

import LoginIndex from './login';

interface IProps {
  children: ReactNode;
  routeProtected: boolean;
}

const defaultClientName = process.env.NEXT_PUBLIC_CLIENT_NAME;

const ProtectRoute = ({ children, routeProtected }: IProps): JSX.Element => {
  const { isLoading: isClientEnvironmentLoading, data: clientEnvironment } =
    useClientEnvironment();

  const { isAuthenticated, sessionLoading } = useAuth();
  const { onboardingLoading } = useOnboardingStateContext();

  const { jobs_domain, name, favicon_logo_url } = clientEnvironment || {};
  const title = jobs_domain
    ? `${extractSubdomainfromUrl(jobs_domain)} | ${name || defaultClientName}`
    : 'Loading...';

  return (
    <>
      <Head>
        <link rel='icon' href={favicon_logo_url} key='favicon' />
        <title>{title}</title>
      </Head>
      <ProtectRouteContent
        onboardingLoading={onboardingLoading}
        sessionLoading={sessionLoading}
        isAuthenticated={isAuthenticated}
        routeProtected={routeProtected}
        isClientEnvironmentLoading={isClientEnvironmentLoading}
      >
        {children}
      </ProtectRouteContent>
    </>
  );
};

type ProtectRouteContentProps = {
  children: ReactNode;
  onboardingLoading: boolean;
  sessionLoading: boolean;
  isAuthenticated: boolean;
  routeProtected: boolean;

  isClientEnvironmentLoading: boolean;
};
const ProtectRouteContent = ({
  children,
  onboardingLoading,
  sessionLoading,
  isAuthenticated,
  routeProtected,

  isClientEnvironmentLoading,
}: ProtectRouteContentProps) => {
  if (onboardingLoading || sessionLoading || isClientEnvironmentLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated && routeProtected) return <LoginIndex />;
  return <>{children}</>;
};

const LoadingScreen = (): JSX.Element => {
  const { data: clientEnvironment, isLoading: isLoadingClientEnvironment } =
    useClientEnvironment();
  const { isEveryset } = clientEnvironment || {};

  if (isLoadingClientEnvironment) {
    return (
      <StyledContainer>
        <StyledSpin size='large' />
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      {isEveryset ? <EverysetLogo width='20vw' /> : <SaaSLogo />}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  height: 100vh;
  display: grid;
  place-content: center;
`;

const StyledSpin = styled(Spin)`
  .ant-spin-dot {
    font-size: 60px;
    .ant-spin-dot-item {
      width: 24px;
      height: 24px;
    }
  }
`;

export default ProtectRoute;
